import React, { useState, useRef } from 'react';
import { Mic, StopCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface AudioRecordButtonProps {
  onRecordingComplete: (audio: Blob) => void;
  isProcessing: boolean;
  className?: string;
}

const AudioRecordButton: React.FC<AudioRecordButtonProps> = ({ 
  onRecordingComplete, 
  isProcessing,
  className = '' 
}) => {
  const { t } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });
      
      mediaRecorder.addEventListener('stop', () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        onRecordingComplete(audioBlob);
        
        // Stop all audio tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Reset recording timer
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setRecordingTime(0);
      });
      
      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      
      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert(t('common.audioRecord.microphoneError'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {isRecording ? (
        <>
          <span className="text-sm font-medium text-red-600">
            {formatTime(recordingTime)}
          </span>
          <button
            type="button"
            onClick={stopRecording}
            className="inline-flex items-center justify-center p-1.5 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
            title={t('common.audioRecord.stop')}
          >
            <StopCircle className="w-5 h-5" />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={startRecording}
          disabled={isProcessing}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
            isProcessing 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
          }`}
          title={t('common.audioRecord.start')}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('common.audioRecord.processing')}
            </>
          ) : (
            <>
              <Mic className="w-4 h-4" />
              {t('common.audioRecord.dictate')}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default AudioRecordButton;